import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Search as SearchIcon, Loader2, FileText, Users, MessageSquare, HelpCircle, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Search = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query || query.length < 2) return;

    setLoading(true);
    setSearched(true);

    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
      setResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return (
      (results.representatives?.length || 0) +
      (results.content?.length || 0) +
      (results.tools?.length || 0) +
      (results.templates?.length || 0) +
      (results.faqs?.length || 0) +
      (results.articles?.length || 0)
    );
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Search
          </h1>
          <p className="text-lg text-slate-600">
            Search across representatives, educational content, engagement tools, templates, and FAQs
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Enter your search query..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="global-search-input"
                className="w-full pl-12 pr-4 h-14 rounded-xl border-2 border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-lg"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={loading || query.length < 2}
              className="bg-primary-700 hover:bg-primary-900 text-white rounded-xl px-8"
              data-testid="search-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <SearchIcon className="mr-2 h-5 w-5" />
                  Search
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Results */}
        {searched && !loading && (
          <div>
            <div className="mb-6">
              <p className="text-slate-600">
                Found <span className="font-semibold text-slate-900">{getTotalResults()}</span> results for "{query}"
              </p>
            </div>

            {getTotalResults() === 0 ? (
              <div className="text-center py-12 bg-surface rounded-xl">
                <SearchIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-2">No results found</p>
                <p className="text-slate-500">Try adjusting your search query</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Representatives */}
                {results.representatives && results.representatives.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Users className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        Representatives ({results.representatives.length})
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.representatives.map((rep) => (
                        <Link key={rep.id} to="/representatives">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{rep.name}</h3>
                            <p className="text-sm text-slate-600">{rep.constituency}, {rep.state}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Educational Content */}
                {results.content && results.content.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <FileText className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        Educational Content ({results.content.length})
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {results.content.map((item) => (
                        <Link key={item.id} to="/legislature">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{item.content}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Engagement Tools */}
                {results.tools && results.tools.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <MessageSquare className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        Engagement Tools ({results.tools.length})
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.tools.map((tool) => (
                        <Link key={tool.id} to="/engagement-tools">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{tool.title}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{tool.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Templates */}
                {results.templates && results.templates.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <FileText className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        Templates ({results.templates.length})
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.templates.map((template) => (
                        <Link key={template.id} to="/templates">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{template.title}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{template.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* FAQs */}
                {results.faqs && results.faqs.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <HelpCircle className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        FAQs ({results.faqs.length})
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {results.faqs.map((faq) => (
                        <Link key={faq.id} to="/faqs">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{faq.question}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{faq.answer}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Articles */}
                {results.articles && results.articles.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <BookOpen className="h-5 w-5 text-primary-700" />
                      <h2 className="font-heading text-2xl font-semibold text-slate-900">
                        Articles ({results.articles.length})
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.articles.map((article) => (
                        <Link key={article.id} to="/articles">
                          <div className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                            <h3 className="font-semibold text-slate-900 mb-1">{article.title}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                            <p className="text-xs text-slate-500 mt-2">By {article.author}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
